import type { Session } from '@synonymdev/pubky';
import { userUriBuilder } from 'pubky-app-specs';
import type { TKeypairParams, TRestoreSessionParams, TRestoreSessionResult } from '@/application/auth/auth.types';
import { ValidationErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import {
  isAppError,
  isNotFound,
  isRetryable,
  isWrongEnvironmentHomeserverError,
  toAppError,
} from '@/libs/error/error.utils';
import { HttpMethod } from '@/libs/http/http.types';
import { Logger } from '@/libs/logger/logger';
import { sleep } from '@/libs/utils/utils';
import type { Pubky } from '@/models/models.types';
import { HomeserverService } from '@/services/homeserver/homeserver';
import type {
  TGenerateAuthUrlResult,
  THomeserverPublicKeyParams,
  THomeserverSessionResult,
  THomeserverSignUpParams,
} from '@/services/homeserver/homeserver.types';

export class AuthApplication {
  private constructor() {} // Prevent instantiation

  private static restoreSessionPromise: TRestoreSessionResult | null = null;

  /** Max attempts before falling back to sign-out (~30 s with a 3 s delay between each) */
  private static readonly RESTORE_MAX_ATTEMPTS = 10;
  /** Fixed delay between retry attempts */
  private static readonly RESTORE_RETRY_DELAY_MS = 3000;

  /**
   * Max attempts for the post-restore profile check (~6 s with a 3 s delay between each).
   * Shorter than the session restore policy: the session is already valid here, this only
   * decides whether the restored account still has a profile.
   */
  private static readonly PROFILE_CHECK_MAX_ATTEMPTS = 3;
  /** Fixed delay between profile-check attempts */
  private static readonly PROFILE_CHECK_RETRY_DELAY_MS = 3000;

  /**
   * Restores a session from a persisted session export.
   * Prevents concurrent restoration attempts by managing a singleton promise.
   *
   * Retries on transient errors (network, timeout, server) to handle scenarios
   * like ERR_NETWORK_CHANGED when the browser tab is resumed or the device
   * reconnects. Non-retryable errors (e.g. genuinely expired session) bail out
   * immediately. After all attempts are exhausted the session is cleared so the
   * user is signed out rather than left on a loading spinner.
   *
   * @param authStore - The auth store object containing state and actions needed for restoration
   * @returns The restored session, or null if restoration failed
   */
  static async restorePersistedSession({ authStore }: TRestoreSessionParams): TRestoreSessionResult {
    // If a restoration is already in progress, return the existing promise
    if (this.restoreSessionPromise) {
      return await this.restoreSessionPromise;
    }

    // Safety check: if sessionExport is missing, return null
    if (!authStore.sessionExport && !authStore.storedSessionId) {
      if (authStore.isRestoringSession) authStore.setIsRestoringSession(false);
      return null;
    }

    // Start restoration and store the promise so concurrent calls can await the same one
    this.restoreSessionPromise = (async () => {
      authStore.setIsRestoringSession(true);

      try {
        // The restored session is kept across attempts so a transient
        // environment-check failure retries only the PKARR lookup instead of
        // re-running the whole restore round-trip.
        let session: Session | null = null;
        for (let attempt = 1; attempt <= this.RESTORE_MAX_ATTEMPTS; attempt++) {
          try {
            session ??= authStore.storedSessionId
              ? await HomeserverService.restoreStoredSession(authStore.storedSessionId)
              : await HomeserverService.restoreSession({ sessionExport: authStore.sessionExport! });
            // Transient lookup failures fall through to the shared retry-or-cleanup
            // policy below — keeping the store in a half-restored state would
            // strand useAuthStatus in its loading branch with no retry trigger.
            await HomeserverService.assertUserHomeserverAllowed({ publicKey: session.info.publicKey });
            Logger.info('Session restored successfully');
            return { session };
          } catch (error) {
            if (isWrongEnvironmentHomeserverError(error)) {
              // The session is about to be discarded and its persisted export
              // erased — sign it out on its own homeserver so it is not left
              // dangling there. Best-effort: the rejection surfaces anyway.
              if (session) {
                await HomeserverService.logout({ session }).catch((logoutError) => {
                  Logger.warn('Failed to sign out wrong-environment session', { logoutError });
                });
              }
              throw error;
            }

            const canRetry = isAppError(error) && isRetryable(error) && attempt < this.RESTORE_MAX_ATTEMPTS;
            if (!canRetry) {
              Logger.error('Failed to restore session from persisted export', error);
              break;
            }

            Logger.warn(
              `Session restore attempt ${attempt}/${this.RESTORE_MAX_ATTEMPTS} failed with transient error, retrying in ${this.RESTORE_RETRY_DELAY_MS}ms`,
              { error },
            );
            await sleep(this.RESTORE_RETRY_DELAY_MS);
          }
        }
        return null;
      } finally {
        authStore.setIsRestoringSession(false);
        this.restoreSessionPromise = null;
      }
    })();

    return await this.restoreSessionPromise;
  }

  /**
   * Signs up a new user in the homeserver with the provided keypair and authentication credentials.
   *
   * @param params - The authentication parameters containing user credentials
   * @param params.keypair - The cryptographic keypair for the user
   * @param params.signupToken - Invitation code for user registration
   * @param params.secretKey - Secret key for homeserver service
   * @returns Session and pubky of the signed up user
   */
  static async signUp({ keypair, signupToken }: THomeserverSignUpParams): Promise<THomeserverSessionResult> {
    return await HomeserverService.signUp({ keypair, signupToken });
  }

  /**
   * Verifies a signup token (invite code) against the homeserver.
   *
   * @param signupToken - The signup token / invite code to verify
   * @returns `'valid'`, `'used'`, or `'invalid'` depending on the homeserver response
   */
  static async verifySignupToken(signupToken: string) {
    return await HomeserverService.verifySignupToken(signupToken);
  }

  /**
   * Authenticates the user against the homeserver using their cryptographic keypair.
   *
   * @param params - The authentication parameters
   * @param params.keypair - The cryptographic keypair for the user authentication
   * @param params.secretKey - Secret key for homeserver service
   * @returns Session and pubky of the authenticated user
   */
  static async signIn({ keypair }: TKeypairParams): Promise<THomeserverSessionResult | undefined> {
    if (!keypair) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        'Keypair not found in onboarding store. Please regenerate your keys and try again.',
        {
          service: ErrorService.Local,
          operation: 'signIn',
        },
      );
    }
    return await HomeserverService.signIn({ keypair });
  }

  /**
   * Generates an authentication URL for Pubky Ring App
   *
   * @returns Authentication URL and approval promise
   */
  static async generateAuthUrl(): Promise<TGenerateAuthUrlResult> {
    return await HomeserverService.generateAuthUrl();
  }

  /**
   * Generates a signup authentication URL for Pubky Ring App.
   * Decorates a standard auth URL with homeserver address and invite code metadata.
   *
   * @param inviteCode - The invite code for signup
   * @returns Authentication URL and approval promise
   */
  static async generateSignupAuthUrl(inviteCode: string): Promise<TGenerateAuthUrlResult> {
    return await HomeserverService.generateSignupAuthUrl({ inviteCode });
  }

  static async saveSession(session: Session): Promise<string> {
    return HomeserverService.saveSession(session);
  }

  static async removeStoredSession(id: string): Promise<void> {
    return HomeserverService.removeStoredSession(id);
  }

  /**
   * Logs out a user from the system.
   *
   * @param params - The logout parameters
   * @param params.session - The authenticated Session
   * @returns Void
   */
  static async logout(data: THomeserverSessionResult) {
    await HomeserverService.logout(data);
  }

  /**
   * Generates a signup token for user registration.
   * @returns Promise resolving to the generated signup token
   */
  static async generateSignupToken() {
    return await HomeserverService.generateSignupToken();
  }

  /** Staging guard: reject keys whose PKARR homeserver does not match this deploy. */
  static async assertUserHomeserverAllowed({ publicKey }: THomeserverPublicKeyParams): Promise<void> {
    await HomeserverService.assertUserHomeserverAllowed({ publicKey });
  }

  /**
   * In the application, there are two signups to do.
   * 1. First the user has to register the user key in the homeserver, throw the inviation code
   * 2. Then the user has to create a profile.json file in the homeserver. That file acts as a proof that the user has signed up.
   * This is a critical step because after that it will start indexing all user related data
   *
   * @param params - Parameters containing the user's public key
   * @param params.pubky - The user's public key identifier
   * @returns Promise resolving to the user profile or undefined if not found
   */
  static async userIsSignedUp({ pubky }: { pubky: Pubky }): Promise<boolean> {
    try {
      await HomeserverService.request({ method: HttpMethod.GET, url: userUriBuilder(pubky) });
      return true;
    } catch (error) {
      const appError = isAppError(error) ? error : toAppError(error, ErrorService.Homeserver, 'userIsSignedUp');
      if (isNotFound(appError)) return false;
      throw appError;
    }
  }

  /**
   * Resolves whether a restored session's profile.json exists, retrying transient homeserver
   * failures so a temporary outage is never mistaken for a missing profile (issue #2070).
   *
   * @param params - Parameters containing the user's public key
   * @param params.pubky - The user's public key identifier
   * @returns true when the profile exists, false when the homeserver confirmed it does not, and
   * null when the state could not be determined after the retries
   */
  static async resolveUserIsSignedUp({ pubky }: { pubky: Pubky }): Promise<boolean | null> {
    for (let attempt = 1; attempt <= this.PROFILE_CHECK_MAX_ATTEMPTS; attempt++) {
      try {
        return await this.userIsSignedUp({ pubky });
      } catch (error) {
        const appError = isAppError(error)
          ? error
          : toAppError(error, ErrorService.Homeserver, 'resolveUserIsSignedUp');
        const canRetry = isRetryable(appError) && attempt < this.PROFILE_CHECK_MAX_ATTEMPTS;
        if (!canRetry) {
          return null;
        }
        Logger.warn(
          `Profile check attempt ${attempt}/${this.PROFILE_CHECK_MAX_ATTEMPTS} failed with a transient error, retrying in ${this.PROFILE_CHECK_RETRY_DELAY_MS}ms`,
          { error: appError },
        );
        await sleep(this.PROFILE_CHECK_RETRY_DELAY_MS);
      }
    }
    return null;
  }
}
